import { UnsubscribeController } from '@personal-area/global/common'
import { Country } from '../../models/country';
import { CountriesFacade } from '../../state/facades/countries';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  BehaviorSubject,
  Observable,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

type MaybeString = string | null;

@Component({
  selector: 'global-country-select',
  standalone: true,
  imports: [CommonModule, NgSelectModule, ReactiveFormsModule],
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectComponent),
      multi: true,
    },
    CountriesFacade,
  ],
})
export class CountrySelectComponent
  extends UnsubscribeController
  implements ControlValueAccessor
{
  @Input() labelForId: MaybeString = null;
  @Input() loadingText = '';
  @Input() placeholder = '';
  @Input() notFoundText = '';
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();

  // Proxy control is the only way to set ng-select value programmatically
  protected _proxyControl = new FormControl<MaybeString>(null);
  protected _countries$: Observable<Country[]>;
  protected _isDisabled$ = new BehaviorSubject(false);
  protected _onTouch = () => {};

  constructor(countryFacade: CountriesFacade) {
    super();
    countryFacade.getCountries();
    this._countries$ = countryFacade.isLoaded$.pipe(
      filter(Boolean),
      switchMap(() => countryFacade.sortedCountries$)
    );
  }

  public writeValue(countryCode: MaybeString): void {
    this._proxyControl.setValue(countryCode);
  }

  public registerOnChange(fn: (countryCode: MaybeString) => void): void {
    this._proxyControl.valueChanges
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._isDisabled$.next(isDisabled);
  }

  public onBlur(): void {
    this._onTouch();
    this.blur.emit();
  }

  public onFocus(): void {
    this.focus.emit();
  }

  protected _trackBy = ({ codeA2 }: Country): string => codeA2;
}
